import React, { useRef, useState } from "react";
import { parsePermit, createPermit, addPermitLimitsBatch, uploadDocument, createCompany, createParameter } from "../api/client";

interface Props {
  companies: { id: number; name: string }[];
  parameters: { id: number; name: string }[];
  // The system's monitoring frequencies, for the per-row Frequency picker.
  frequencies?: { id: number; frequency_code: string; description?: string | null }[];
  onImported: (msg: string) => void;
  onCancel: () => void;
  // When set, limits are imported INTO this existing permit (upsert) instead of
  // creating a new one. The company/permit#/date fields are then hidden.
  targetPermit?: { id: number; permit_number: string };
  // Started from a company's panel: that company is chosen to begin with.
  defaultCompanyId?: number;
  // A company was created from the permit on this screen (so the caller can refresh its list).
  onCompanyCreated?: (company: { id: number; name: string }) => void;
  // The permits already on file, so a second permit for the same company is caught.
  existingPermits?: { id: number; company_id: number; permit_number: string; status?: string;
                      effective_date: string; expiration_date: string }[];
  // A parameter was added from the "Maps to" picker (so the caller can refresh its list).
  onParameterCreated?: (parameter: { id: number; name: string }) => void;
}

// The six limit cells of a row: [key in the parsed row, column heading, field on the saved limit].
const VALUE_CELLS = [
  ["daily_max_conc",      "Daily max (conc)",  "daily_max_concentration"],
  ["daily_max_loading",   "Daily max (load)",  "daily_max_loading"],
  ["weekly_avg_conc",     "Wkly avg (conc)",   "weekly_max_concentration"],
  ["weekly_avg_loading",  "Wkly avg (load)",   "weekly_max_loading"],
  ["monthly_avg_conc",    "Mo. avg (conc)",    "monthly_avg_concentration"],
  ["monthly_avg_loading", "Mo. avg (load)",    "monthly_avg_loading"],
] as const;
type ValueKey = typeof VALUE_CELLS[number][0];

type Row = {
  parameter: string;
  parameter_id: number | null;
  matched_name: string | null;
  daily_max_conc: string;
  daily_max_loading: string;
  weekly_avg_conc: string;
  weekly_avg_loading: string;
  monthly_avg_conc: string;
  monthly_avg_loading: string;
  // Cells the permit marks "MR" (monitor and report): sampled and reported, no numeric limit.
  mr: Record<ValueKey, boolean>;
  sample_type: string;
  frequency: string;              // the frequency as printed on the permit ("1/ Month")
  frequency_id: number | null;    // the system frequency it maps to; null = leave unset
  note: string;                   // the reader's remark on the row (units, "Monitor & Report", a range…)
  warnings: string[];             // where the reading disagrees with the PDF's own text
  // A flow row, imported as a flow-type limit (MGD) for one averaging period.
  is_flow_limit: boolean;
  // pH: a range limit (min/max in s.u.) instead of concentration/loading cells.
  is_range_limit: boolean;
  min_value: string;
  max_value: string;
  averaging_period: string | null;
  // The permit's limits table the row is printed in ("Interim limits", "Final limits"), if it has several.
  table: string | null;
  include: boolean;
};

type Table = { label: string; rows: number; first_page: number | null };

// Tick the matched rows of the chosen table, each parameter once: a later row
// mapped to a parameter already ticked starts unticked (a permit has one limit each).
const tickRows = (rows: Row[], table: string | null, multiTable: boolean): Row[] => {
  const ticked = new Set<number>();
  return rows.map(r => {
    const include = r.parameter_id != null && (!multiTable || r.table === null || r.table === table)
      && !ticked.has(r.parameter_id);
    if (include) ticked.add(r.parameter_id!);
    return { ...r, include };
  });
};

const numOrNull = (v: string): number | null => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const str = (v: any) => (v === null || v === undefined ? "" : String(v));
const usDate = (iso: string) => { const [y, m, d] = iso.split("-"); return `${m}/${d}/${y}`; };

/**
 * Feature #2 Phase B: upload a discharge-permit PDF, review the parsed limit
 * rows (map each to a parameter, edit values, toggle which to keep), then create
 * a permit and batch-import the limits. Mirrors the COA import flow.
 */
export default function PermitImport({ companies: knownCompanies, parameters: knownParameters, frequencies = [],
                                       onImported, onCancel, targetPermit: fixedTarget, defaultCompanyId,
                                       onCompanyCreated, onParameterCreated, existingPermits = [] }: Props) {
  // "Import into that permit instead" (offered when the company already has one in progress).
  const [intoPermit, setIntoPermit] = useState<{ id: number; permit_number: string } | null>(null);
  const targetPermit = fixedTarget ?? intoPermit ?? undefined;
  const [renewalOk, setRenewalOk] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<any | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [permitNumber, setPermitNumber] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);   // saved on the permit after import
  // A company created here from the permit's details, until the caller's list catches up.
  const [created, setCreated] = useState<{ id: number; name: string }[]>([]);
  const companies = [...knownCompanies, ...created.filter(c => !knownCompanies.some(k => k.id === c.id))];
  const [newCo, setNewCo] = useState<{ name: string; address: string; sic: string; iu: string } | null>(null);
  const [newCoError, setNewCoError] = useState("");
  const [newCoSaving, setNewCoSaving] = useState(false);
  // A parameter the permit lists that the system lacks (e.g. TTO), added on the spot.
  const [createdParams, setCreatedParams] = useState<{ id: number; name: string }[]>([]);
  const parameters = [...knownParameters, ...createdParams.filter(p => !knownParameters.some(k => k.id === p.id))]
    .sort((a, b) => a.name.localeCompare(b.name));
  const [newParam, setNewParam] = useState<{ row: number; name: string; abbr: string } | null>(null);
  const [newParamError, setNewParamError] = useState("");
  const [newParamSaving, setNewParamSaving] = useState(false);
  // Interim and final tables list the same parameters; one table is imported at a time.
  const [tables, setTables] = useState<Table[]>([]);
  const [table, setTable] = useState<string | null>(null);
  const inTable = (r: Row, t: string | null) => tables.length === 0 || r.table === null || r.table === t;

  const handleFile = async (file: File) => {
    setParsing(true); setError(""); setPreview(null);
    setPdfFile(file);
    try {
      const res = await parsePermit(file);
      setPreview(res.data);
      // What the permit itself says: number (with the POTW's prefix), company and term.
      setPermitNumber(res.data.permit_number_suggested ?? res.data.permit_number ?? "");
      setCompanyId(res.data.company_id != null ? String(res.data.company_id)
                    : defaultCompanyId != null ? String(defaultCompanyId) : "");
      setNewCo(null); setNewCoError("");
      setEffectiveDate(res.data.effective_date ?? "");
      setExpirationDate(res.data.expiration_date ?? "");
      const tbls: Table[] = Array.isArray(res.data.tables) ? res.data.tables : [];
      const chosen: string | null = tbls.length ? (res.data.default_table ?? tbls[0].label) : null;
      setTables(tbls);
      setTable(chosen);
      setRows(tickRows((res.data.limits ?? []).map((l: any): Row => ({
        parameter: l.parameter,
        parameter_id: l.parameter_id ?? null,
        matched_name: l.matched_name ?? null,
        daily_max_conc: str(l.daily_max_conc),
        daily_max_loading: str(l.daily_max_loading),
        weekly_avg_conc: str(l.weekly_avg_conc),
        weekly_avg_loading: str(l.weekly_avg_loading),
        monthly_avg_conc: str(l.monthly_avg_conc),
        monthly_avg_loading: str(l.monthly_avg_loading),
        mr: Object.fromEntries(VALUE_CELLS.map(([k]) => [k, !!l[`${k}_mr`]])) as Record<ValueKey, boolean>,
        sample_type: str(l.sample_type),
        frequency: str(l.frequency),
        frequency_id: l.frequency_id ?? null,
        note: str(l.note),
        warnings: Array.isArray(l.warnings) ? l.warnings : [],
        is_flow_limit: !!l.is_flow_limit,
        is_range_limit: !!l.is_range_limit,
        min_value: str(l.min_value),
        max_value: str(l.max_value),
        averaging_period: l.averaging_period ?? null,
        table: l.table ?? null,
        include: false,
      })), chosen, tbls.length > 0));
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Failed to parse permit PDF");
    } finally {
      setParsing(false);
    }
  };

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const chooseTable = (t: string) => {
    setTable(t);
    setRows(rs => tickRows(rs, t, true));
  };

  const included = rows.filter(r => r.include && r.parameter_id && inTable(r, table));
  const includedCount = included.length;

  // Keep the permit PDF itself on the permit (Documents). The limits are already
  // saved, so a failed upload only adds a note to the message.
  const keepPdf = async (permitId: number): Promise<string> => {
    if (!pdfFile) return "";
    try {
      await uploadDocument("permit", permitId, pdfFile, "permit");
      return ` The PDF ${pdfFile.name} is saved in the permit's documents.`;
    } catch {
      return ` (The PDF itself could not be saved — upload it under the permit's documents.)`;
    }
  };

  // What still has to be filled in before the permit can be created (step ②), or
  // before limits can be imported (step ③).
  const missing: string[] = [];
  // The chosen company's permits: one not yet in force over the same term is a
  // duplicate (blocked, as the server does); an active one needs a "yes, renewal".
  const companyPermits = existingPermits.filter(p => String(p.company_id) === companyId);
  const overlaps = (p: { effective_date: string; expiration_date: string }) =>
    !effectiveDate || !expirationDate || (p.effective_date <= expirationDate && p.expiration_date >= effectiveDate);
  const duplicate = companyPermits.find(p => ["draft", "pending", "scheduled"].includes(p.status ?? "") && overlaps(p));
  const activePermit = companyPermits.find(p => p.status === "active");
  // A permit number belongs to one company. The same company reusing it is a
  // renewal and fine; any other company needs a different number.
  const numberHolderId: number | null = preview?.permit_number_company_id ?? null;
  const numberHolder = companies.find(c => c.id === numberHolderId);
  const numberTaken = numberHolderId != null && String(numberHolderId) !== companyId
    && permitNumber.trim().toUpperCase() === (preview?.permit_number_suggested ?? "").toUpperCase();
  if (!targetPermit) {
    if (!companyId) missing.push("choose or create the company");
    if (!permitNumber.trim()) missing.push("enter the permit number");
    if (!effectiveDate || !expirationDate) missing.push("enter the effective and expiration dates");
    if (duplicate) missing.push(`this company already has ${duplicate.status} permit ${duplicate.permit_number}`);
    else if (activePermit && !renewalOk) missing.push("confirm this is a renewal or replacement");
  }

  const startNewCompany = () => {
    const sic = (preview?.sic_codes ?? []).join(", ");
    setNewCo({ name: preview?.permittee_name ?? "", address: preview?.permittee_address ?? "",
               sic: sic.length <= 10 ? sic : (preview?.sic_codes?.[0] ?? ""),
               iu: numberHolderId != null ? "" : (preview?.permit_number_suggested ?? "") });
    setNewCoError("");
  };

  const openNewParam = (i: number) => {
    const name = (rows[i].parameter || "").replace(/\s+/g, " ").trim();
    const short = name.match(/\(([A-Za-z0-9&]{1,10})\)/)?.[1]
      ?? (name.split(" ").length > 1 ? name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 10) : name.slice(0, 10));
    setNewParam({ row: i, name: name.replace(/\s*\([^)]*\)\s*$/, ""), abbr: short.toUpperCase() });
    setNewParamError("");
  };

  const saveNewParam = async () => {
    if (!newParam) return;
    if (!newParam.name.trim() || !newParam.abbr.trim()) return setNewParamError("Enter a name and an abbreviation.");
    setNewParamSaving(true); setNewParamError("");
    try {
      const r = await createParameter({ name: newParam.name.trim(), abbreviation: newParam.abbr.trim() });
      const p = { id: r.data.id, name: r.data.name };
      setCreatedParams(ps => [...ps, p]);
      setRow(newParam.row, { parameter_id: p.id, include: true });
      setNewParam(null);
      onParameterCreated?.(p);
    } catch (e: any) {
      setNewParamError(e.response?.data?.error ?? "Couldn't add the parameter.");
    } finally {
      setNewParamSaving(false);
    }
  };

  const saveNewCompany = async () => {
    if (!newCo) return;
    if (!newCo.name.trim()) return setNewCoError("Enter the company name.");
    setNewCoSaving(true); setNewCoError("");
    try {
      const r = await createCompany({ name: newCo.name.trim(), address: newCo.address.trim() || null,
                                      sic_code: newCo.sic.trim() || null, iu_permit_number: newCo.iu.trim() || null });
      const c = { id: r.data.id, name: r.data.name };
      setCreated(cs => [...cs, c]);
      setCompanyId(String(c.id));
      setNewCo(null);
      onCompanyCreated?.(c);
    } catch (e: any) {
      setNewCoError(e.response?.data?.error ?? "Couldn't create the company.");
    } finally {
      setNewCoSaving(false);
    }
  };

  const handleImport = async () => {
    setError("");
    if (includedCount === 0) return setError("No rows selected to import (a row needs a mapped parameter and its checkbox ticked).");
    // A permit holds one limit per parameter.
    const seen = new Set<number>();
    const twice = included.find(r => seen.has(r.parameter_id!) || !seen.add(r.parameter_id!));
    if (twice) {
      const name = parameters.find(p => p.id === twice.parameter_id)?.name ?? twice.parameter;
      return setError(`${name} is ticked on more than one row. A permit has one limit per parameter — untick all but one.`);
    }
    if (!targetPermit) {
      if (!companyId) return setError("Choose a company.");
      if (!permitNumber.trim()) return setError("Enter a permit number.");
      if (!effectiveDate || !expirationDate) return setError("Enter the effective and expiration dates.");
    }

    setImporting(true);
    try {
      const items = included.map(r => {
        // An MR cell has no number; its column is flagged monitor-and-report instead.
        const cells: Record<string, number | boolean | null> = {};
        let anyNumber = false, anyMr = false;
        for (const [key, , field] of VALUE_CELLS) {
          const value = r.mr[key] ? null : numOrNull(r[key]);
          cells[field] = value;
          cells[`${field}_is_mr`] = r.mr[key];
          anyNumber = anyNumber || value !== null;
          anyMr = anyMr || r.mr[key];
        }
        if (r.is_range_limit) {
          // pH: a min/max range in s.u.; no concentration or loading cells.
          return {
            parameter_id: r.parameter_id,
            ...Object.fromEntries(VALUE_CELLS.flatMap(([, , field]) => [[field, null], [`${field}_is_mr`, false]])),
            is_range_limit: true, min_value: numOrNull(r.min_value), max_value: numOrNull(r.max_value),
            range_unit: "s.u.", is_monitor_report: false,
            sample_type: r.sample_type || null,
            ...(r.frequency_id != null ? { frequency_id: r.frequency_id } : {}),
          };
        }
        if (r.is_flow_limit) {
          // A flow limit: the MGD value sits in the column for its averaging period.
          for (const [, , field] of VALUE_CELLS) cells[`${field}_is_mr`] = false;
          return {
            parameter_id: r.parameter_id, ...cells,
            is_flow_limit: true, averaging_period: r.averaging_period, is_monitor_report: false,
            sample_type: r.sample_type || null,
            ...(r.frequency_id != null ? { frequency_id: r.frequency_id } : {}),
          };
        }
        return {
        parameter_id: r.parameter_id,
        ...cells,
        // Monitor-only parameter: MR cells and no numeric limit anywhere.
        is_monitor_report: anyMr && !anyNumber,
        sample_type: r.sample_type || null,
        // Only sent when chosen, so importing into an existing permit never
        // blanks a frequency that is already set there.
        ...(r.frequency_id != null ? { frequency_id: r.frequency_id } : {}),
        };
      });
      if (targetPermit) {
        // Import limits INTO the existing permit (upsert: fill blank params, add new).
        const res = await addPermitLimitsBatch(targetPermit.id, items, true);
        const added = res.data?.created?.length ?? 0;
        const updated = res.data?.updated?.length ?? 0;
        const skipped = res.data?.errors?.length ?? 0;
        const saved = await keepPdf(targetPermit.id);
        onImported(`Imported into permit ${targetPermit.permit_number}: ${added} added, ${updated} updated`
          + (skipped ? `, ${skipped} skipped` : "") + " — from the PDF." + saved);
      } else {
        const permit = await createPermit({
          company_id: parseInt(companyId),
          permit_number: permitNumber.trim(),
          effective_date: effectiveDate,
          expiration_date: expirationDate,
        });
        const res = await addPermitLimitsBatch(permit.data.id, items);
        const added = res.data?.created?.length ?? items.length;
        const skipped = res.data?.errors?.length ?? 0;
        const saved = await keepPdf(permit.data.id);
        onImported(`Imported permit ${permitNumber.trim()} with ${added} limit(s)`
          + (table ? ` (${table.toLowerCase()})` : "") + " from the PDF."
          + (skipped ? ` ${skipped} row(s) were not imported.` : "") + saved);
      }
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Failed to import permit");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={s.wrap}>
      {!preview && (
        <>
          <div style={s.step}>① Upload the permit PDF</div>
          <div style={s.dropzone}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}>
            {parsing ? "Parsing permit…" : (
              <>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <strong>Click or drag a discharge-permit PDF here</strong>
                <div style={{ fontSize: 12, marginTop: 4, color: "#718096" }}>
                  {targetPermit
                    ? "Its limits are read and matched to your parameters for you to check before importing."
                    : "The company, permit number, dates and limits are read from it for you to check — the permit is created at the end."}
                </div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {error && <p style={{ color: "#c53030", fontSize: 13 }}>{error}</p>}
          <button style={s.outBtn} onClick={onCancel}>Cancel</button>
        </>
      )}

      {preview && (
        <>
          <div style={s.summary}>
            <span style={chip("#2c5282", "#bee3f8")}>{preview.limits?.length ?? 0} rows parsed</span>
            {table && <span style={chip("#2c5282", "#bee3f8")}>showing {table.toLowerCase()}</span>}
            <span style={chip("#276749", "#c6f6d5")}>{preview.matched} matched</span>
            <span style={chip("#c53030", "#fed7d7")}>{preview.unmatched} unmatched</span>
            {preview.sic_codes?.length > 0 && (
              <span style={chip("#553c9a", "#e9d8fd")}>SIC: {preview.sic_codes.join(", ")}</span>
            )}
          </div>

          {preview.reader === "claude" && (
            <p style={{ fontSize: 13, color: "#553c9a", background: "#faf5ff", border: "1px solid #d6bcfa",
                        borderRadius: 6, padding: "8px 10px", margin: "4px 0 12px" }}>
              Read by <strong>Claude (AI)</strong> from the PDF. It can misread a value, so check every row
              against the permit before importing.
              {preview.text_checked
                ? (preview.warning_rows > 0
                    ? <> The reading was compared with the PDF's text: <strong>{preview.warning_rows} row(s) need a closer look</strong> (marked ⚠).</>
                    : <> The reading was compared with the PDF's text and no differences were found.</>)
                : <> This PDF has no readable text (a scan), so the reading could <strong>not</strong> be cross-checked.</>}
            </p>
          )}
          {preview.reader_note && (
            <p style={{ fontSize: 13, color: "#7b341e", background: "#fffaf0", border: "1px solid #fbd38d",
                        borderRadius: 6, padding: "8px 10px", margin: "4px 0 12px" }}>
              {preview.reader_note}
            </p>
          )}
          <div style={s.step}>② {targetPermit ? "The permit" : "Who and when"}</div>
          {targetPermit ? (
            <p style={{ fontSize: 13, color: "#2c5282", margin: "4px 0 12px" }}>
              Importing limits into existing permit <strong>{targetPermit.permit_number}</strong>.
              Parameters already on the permit are updated with the parsed values; new ones are added.
              {intoPermit && (
                <button type="button" style={{ ...s.linkBtn }} onClick={() => setIntoPermit(null)}>
                  ← back to creating a new permit</button>
              )}
            </p>
          ) : (
            <>
            {(preview.permittee_name || preview.permit_number || preview.effective_date) && (
              <div style={s.readFrom}>
                <strong>Read from the permit:</strong>{" "}
                {[preview.permittee_name,
                  preview.permit_number && `Permit No. ${preview.permit_number}`,
                  preview.effective_date && `effective ${usDate(preview.effective_date)}`,
                  preview.expiration_date && `expires ${usDate(preview.expiration_date)}`]
                  .filter(Boolean).join(" · ")}
                <div style={{ marginTop: 3 }}>
                  {preview.company_id != null
                    ? <>Matched to <strong>{companies.find(c => c.id === preview.company_id)?.name}</strong> by its {preview.company_matched_by}. Check it below.</>
                    : companyId
                      ? <>Company: <strong>{companies.find(c => String(c.id) === companyId)?.name}</strong>. Check it below.</>
                    : preview.permittee_name
                      ? <span style={{ color: "#975a16" }}>“{preview.permittee_name}” is not one of your companies yet — create it here, or choose it below.</span>
                      : null}
                  {defaultCompanyId != null && preview.company_id != null && preview.company_id !== defaultCompanyId && (
                    <div style={{ color: "#c05621" }}>
                      You started from {companies.find(c => c.id === defaultCompanyId)?.name}, but this permit is for
                      another company — check the Company below.
                    </div>
                  )}
                </div>
                {numberTaken && (
                  <div role="alert" style={{ color: "#c53030", marginTop: 3 }}>
                    Permit number {preview.permit_number_suggested} is already used by{" "}
                    {numberHolder?.name ?? "another company"}. If this is their renewal, choose them as the
                    Company below; otherwise enter a different permit number.
                  </div>
                )}
              </div>
            )}
            {!companyId && preview.permittee_name && !newCo && (
              <button type="button" style={s.createCo} onClick={startNewCompany}>
                ＋ Create “{preview.permittee_name}” from this permit
              </button>
            )}
            {newCo && (
              <div style={s.newCo}>
                <strong style={{ fontSize: 13 }}>New company, from the permit — check and create</strong>
                <div style={s.targetGrid}>
                  <label style={s.field}><span style={s.label}>Company name</span>
                    <input style={{ ...s.input, minWidth: 240 }} value={newCo.name}
                      onChange={e => setNewCo({ ...newCo, name: e.target.value })} /></label>
                  <label style={s.field}><span style={s.label}>Address</span>
                    <input style={{ ...s.input, minWidth: 280 }} value={newCo.address}
                      onChange={e => setNewCo({ ...newCo, address: e.target.value })} /></label>
                  <label style={s.field}><span style={s.label}>SIC</span>
                    <input style={{ ...s.input, width: 110 }} value={newCo.sic} maxLength={10}
                      onChange={e => setNewCo({ ...newCo, sic: e.target.value })} /></label>
                  <label style={s.field}><span style={s.label}>IU permit #</span>
                    <input style={{ ...s.input, width: 130 }} value={newCo.iu} placeholder="blank = next number"
                      onChange={e => setNewCo({ ...newCo, iu: e.target.value })} /></label>
                </div>
                <div style={{ fontSize: 12, color: "#718096", margin: "-4px 0 8px" }}>
                  The IU permit number is permanent once set. Contact details and the SCDES report facts can be
                  added later under Companies → Edit.
                </div>
                {newCoError && <div role="alert" style={{ color: "#c53030", fontSize: 13, marginBottom: 6 }}>{newCoError}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" style={s.btn} onClick={saveNewCompany} disabled={newCoSaving}>
                    {newCoSaving ? "Creating…" : "Create company"}</button>
                  <button type="button" style={s.outBtn} onClick={() => setNewCo(null)}>Cancel</button>
                </div>
              </div>
            )}
            <div style={s.targetGrid}>
              <label style={s.field}><span style={s.label}>Company</span>
                <select style={s.input} value={companyId} onChange={e => setCompanyId(e.target.value)}>
                  <option value="">— select —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label style={s.field}><span style={s.label}>Permit #</span>
                <input style={s.input} value={permitNumber} onChange={e => setPermitNumber(e.target.value)} />
              </label>
              <label style={s.field}><span style={s.label}>Effective</span>
                <input style={s.input} type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
              </label>
              <label style={s.field}><span style={s.label}>Expiration</span>
                <input style={s.input} type="date" value={expirationDate} onChange={e => setExpirationDate(e.target.value)} />
              </label>
            </div>
            {duplicate ? (
              <div role="alert" style={s.dupBox}>
                <strong>A permit already exists for this company.</strong>{" "}
                {companies.find(c => String(c.id) === companyId)?.name} already has a {duplicate.status} permit,{" "}
                <strong>{duplicate.permit_number}</strong> ({usDate(duplicate.effective_date)} – {usDate(duplicate.expiration_date)}),
                that is not in force yet. A second one would be a duplicate.
                <div style={{ marginTop: 6 }}>
                  <button type="button" style={s.createCo}
                    onClick={() => setIntoPermit({ id: duplicate.id, permit_number: duplicate.permit_number })}>
                    Import these limits into {duplicate.permit_number} instead
                  </button>
                </div>
              </div>
            ) : activePermit && (
              <div role="alert" style={s.renewBox}>
                <strong>A permit already exists for this company.</strong>{" "}
                <strong>{activePermit.permit_number}</strong> is active (expires {usDate(activePermit.expiration_date)}).
                Are you sure you want another? Only for a renewal or replacement: the new permit starts as a
                draft and takes over from {activePermit.permit_number} only when you activate it.
                <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, fontWeight: 600 }}>
                  <input type="checkbox" checked={renewalOk} onChange={e => setRenewalOk(e.target.checked)} />
                  Yes, this is the renewal / replacement of {activePermit.permit_number}
                </label>
              </div>
            )}
            </>
          )}

          <div style={s.step}>③ Limits — check every row against the permit</div>
          {tables.length > 1 && (
            <div style={{ fontSize: 13, background: "#ebf8ff", border: "1px solid #90cdf4", borderRadius: 6,
                          padding: "8px 10px", margin: "4px 0 12px", color: "#2a4365" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong>This permit has {tables.length} limits tables.</strong> Import:
                {tables.map(t => (
                  <label key={t.label} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                    <input type="radio" name="permit-table" checked={table === t.label}
                      onChange={() => chooseTable(t.label)} />
                    {t.label}{t.first_page ? ` (page ${t.first_page})` : ""}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 12, marginTop: 4, color: "#4a5568" }}>
                Interim limits apply from the permit's effective date; final limits apply later, as the
                permit says. A permit holds one limit per parameter, so import the table in force now.
                When the other takes effect, open this permit, choose Import limits from PDF and pick it — the
                limits are updated.
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>✓</th>
                  <th style={s.th}>Parsed parameter</th>
                  <th style={s.th}>Maps to</th>
                  {VALUE_CELLS.map(([key, heading]) => <th key={key} style={s.th}>{heading}</th>)}
                  <th style={s.th}>Sample type</th>
                  <th style={s.th}>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => inTable(r, table) && (
                  <tr key={i} style={{ background: r.parameter_id ? "white" : "#fffaf0" }}>
                    <td style={s.td}>
                      <input type="checkbox" checked={r.include}
                        disabled={!r.parameter_id}
                        onChange={e => setRow(i, { include: e.target.checked })} />
                    </td>
                    <td style={s.td}>
                      {r.parameter}
                      {r.is_flow_limit && (
                        <div style={{ fontSize: 11, color: "#2b6cb0", fontWeight: 600 }}>
                          Flow limit (MGD) · {r.averaging_period === "monthly_avg" ? "monthly average" : "daily maximum"}
                        </div>
                      )}
                      {r.note && <div style={{ fontSize: 11, color: "#718096", maxWidth: 220 }}>{r.note}</div>}
                      {r.warnings.map((w, k) => (
                        <div key={k} role="alert" style={{ fontSize: 11, color: "#c05621", maxWidth: 240, marginTop: 2 }}>⚠ {w}</div>
                      ))}
                    </td>
                    <td style={s.td}>
                      <select style={s.select} value={r.parameter_id ?? ""} aria-label={`${r.parameter} maps to`}
                        onChange={e => {
                          if (e.target.value === "__new__") return openNewParam(i);
                          const id = e.target.value ? parseInt(e.target.value) : null;
                          setRow(i, { parameter_id: id, include: id != null && r.include });
                        }}>
                        <option value="">— skip —</option>
                        {parameters.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        <option value="__new__">＋ New parameter…</option>
                      </select>
                      {newParam?.row === i && (
                        <div style={{ marginTop: 6, padding: 8, background: "#ebf8ff", border: "1px solid #90cdf4",
                                      borderRadius: 5, display: "flex", flexDirection: "column", gap: 4, width: 200 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#2c5282" }}>New parameter</span>
                          <input style={s.cell} value={newParam.name} aria-label="New parameter name" placeholder="Name"
                            onChange={e => setNewParam({ ...newParam, name: e.target.value })} />
                          <input style={s.cell} value={newParam.abbr} aria-label="New parameter abbreviation"
                            placeholder="Abbreviation, e.g. TTO"
                            onChange={e => setNewParam({ ...newParam, abbr: e.target.value })} />
                          {newParamError && <span role="alert" style={{ fontSize: 11, color: "#c53030" }}>{newParamError}</span>}
                          <div style={{ display: "flex", gap: 6 }}>
                            <button type="button" style={{ ...s.select, background: "#2b6cb0", color: "#fff", cursor: "pointer" }}
                              disabled={newParamSaving} onClick={saveNewParam}>
                              {newParamSaving ? "Adding…" : "Add"}
                            </button>
                            <button type="button" style={{ ...s.select, background: "#fff", cursor: "pointer" }}
                              onClick={() => setNewParam(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </td>
                    {r.is_range_limit ? (
                      <td colSpan={VALUE_CELLS.length} style={s.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#4a5568" }}>
                          <span style={{ fontWeight: 600 }}>Range</span>
                          <span>min</span>
                          <input style={s.cell} value={r.min_value} aria-label={`${r.parameter} minimum`}
                            onChange={e => setRow(i, { min_value: e.target.value })} />
                          <span>– max</span>
                          <input style={s.cell} value={r.max_value} aria-label={`${r.parameter} maximum`}
                            onChange={e => setRow(i, { max_value: e.target.value })} />
                          <span>s.u.</span>
                          {(!r.min_value || !r.max_value) && (
                            <span style={{ color: "#b7791f" }}>— fill in from the permit</span>
                          )}
                        </div>
                      </td>
                    ) : VALUE_CELLS.map(([key, heading]) => (
                      <td key={key} style={s.td}>
                        <input style={s.cell} value={r.mr[key] ? "" : r[key]} disabled={r.mr[key]}
                          placeholder={r.mr[key] ? "MR" : ""}
                          aria-label={`${r.parameter} ${heading}`}
                          onChange={e => setRow(i, { [key]: e.target.value } as Partial<Row>)} />
                        <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11,
                                        color: r.mr[key] ? "#c05621" : "#a0aec0", cursor: "pointer", marginTop: 2 }}>
                          <input type="checkbox" checked={r.mr[key]} disabled={r.is_flow_limit}
                            aria-label={`${r.parameter} ${heading} monitor and report`}
                            onChange={e => setRow(i, { mr: { ...r.mr, [key]: e.target.checked } })} />
                          MR
                        </label>
                      </td>
                    ))}
                    <td style={s.td}><input style={s.cell} value={r.sample_type} onChange={e => setRow(i, { sample_type: e.target.value })} /></td>
                    <td style={s.td}>
                      <select style={s.select} value={r.frequency_id ?? ""}
                        aria-label={`${r.parameter} frequency`}
                        onChange={e => setRow(i, { frequency_id: e.target.value ? parseInt(e.target.value) : null })}>
                        <option value="">— not set —</option>
                        {frequencies.map(f => (
                          <option key={f.id} value={f.id}>{f.description ? `${f.description} (${f.frequency_code})` : f.frequency_code}</option>
                        ))}
                      </select>
                      {r.frequency && (
                        <div style={{ fontSize: 11, color: r.frequency_id != null ? "#718096" : "#b7791f" }}>
                          permit says: {r.frequency}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 12, color: "#718096", margin: "12px 0" }}>
            Unmatched rows (amber) need a parameter mapped before they can be included.
            The monitoring frequency is read from the permit where it can be; pick one for any row left "not set".
            A cell the permit marks "MR" (monitor and report) is ticked MR: it is sampled and reported
            in that column with no numeric limit. Other non-numeric values (e.g. "N/A") import as blank. Review every value against the
            source permit — parsing is a draft, not a substitute for the document.
          </p>
          <div style={s.step}>④ {targetPermit ? "Import the limits" : "Create the permit"}</div>
          {missing.length > 0 && (
            <p style={{ fontSize: 13, color: "#975a16", margin: "0 0 8px" }}>
              Before the permit can be created: {missing.join(" · ")} (step ②).
            </p>
          )}
          {error && <p style={{ color: "#c53030", fontSize: 13 }}>{error}</p>}

          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...s.btn, ...(missing.length ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
              disabled={importing || missing.length > 0} onClick={handleImport}>
              {importing ? "Importing…"
                : targetPermit ? `Import ${includedCount} limit(s) into ${targetPermit.permit_number} →`
                : `Create permit & import ${includedCount} limit(s) →`}
            </button>
            <button style={s.outBtn} onClick={() => { setPreview(null); setRows([]); setTables([]); setTable(null);
                                                       setIntoPermit(null); setRenewalOk(false); }}>← Try another file</button>
            <button style={s.outBtn} onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  linkBtn: { background: "none", border: "none", color: "#2b6cb0", textDecoration: "underline",
             cursor: "pointer", fontSize: 13, marginLeft: 6, padding: 0 },
  dupBox: { background: "#fff5f5", border: "1px solid #feb2b2", color: "#742a2a", borderRadius: 6,
            padding: "8px 10px", fontSize: 13, margin: "0 0 12px" },
  renewBox: { background: "#fffaf0", border: "1px solid #fbd38d", color: "#744210", borderRadius: 6,
              padding: "8px 10px", fontSize: 13, margin: "0 0 12px" },
  wrap: { fontFamily: "inherit" },
  dropzone: { border: "2px dashed #4299e1", borderRadius: 8, padding: "32px 24px",
              textAlign: "center", cursor: "pointer", background: "#ebf8ff", color: "#2c5282", marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 4 },
  th: { padding: "6px 8px", background: "#f7fafc", borderBottom: "2px solid #e2e8f0",
        textAlign: "left", fontWeight: 700, color: "#1a202c", whiteSpace: "nowrap" },
  td: { padding: "5px 8px", borderBottom: "1px solid #edf2f7", verticalAlign: "middle" },
  btn: { padding: "9px 22px", background: "#2b6cb0", color: "#fff", border: "none",
         borderRadius: 5, fontWeight: 600, cursor: "pointer" },
  outBtn: { padding: "9px 22px", background: "#fff", color: "#718096", border: "2px solid #cbd5e0",
            borderRadius: 5, fontWeight: 600, cursor: "pointer" },
  select: { padding: "3px 6px", border: "1px solid #cbd5e0", borderRadius: 4, fontSize: 12, maxWidth: 180 },
  cell: { padding: "3px 6px", border: "1px solid #cbd5e0", borderRadius: 4, fontSize: 12, width: 80 },
  summary: { display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" },
  targetGrid: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  step: { fontSize: 14, fontWeight: 700, color: "#1a365d", margin: "14px 0 6px" },
  createCo: { padding: "6px 14px", fontSize: 13, background: "#fff", color: "#2b6cb0", border: "1px dashed #2b6cb0",
              borderRadius: 5, cursor: "pointer", margin: "0 0 10px" },
  newCo: { border: "1px solid #bee3f8", background: "#f7fbff", borderRadius: 6, padding: "10px 12px", margin: "0 0 12px" },
  readFrom: { fontSize: 13, color: "#2a4365", background: "#ebf8ff", border: "1px solid #bee3f8", borderRadius: 6,
              padding: "8px 10px", margin: "4px 0 10px" },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#4a5568" },
  input: { padding: "6px 8px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13 },
};

const chip = (color: string, bg: string): React.CSSProperties => ({
  padding: "4px 12px", borderRadius: 12, fontSize: 12, fontWeight: 700, background: bg, color,
});
